import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Shield, Mail, Globe, Key, Copy, Check, AlertTriangle, 
  Plus, Trash2, TestTube, ToggleLeft, ToggleRight, Send, UserPlus, Users,
  Zap, Info, Timer, Brain, Eye, EyeOff, Loader2, Bell, Lock, X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmergencyPanel } from "@/components/EmergencyPanel";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CORSOriginsSection() {
  const { user } = useAuth();
  const [origins, setOrigins] = useState([]);
  const [envOrigins, setEnvOrigins] = useState([]);
  const [newOrigin, setNewOrigin] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    axios.get(`${API}/settings/cors-origins`)
      .then(r => { setOrigins(r.data.origins || []); setEnvOrigins(r.data.env_origins || []); })
      .catch(() => {});
  }, [user]);

  const addOrigin = () => {
    const url = newOrigin.trim().replace(/\/$/, "");
    if (!url) return;
    if (origins.includes(url)) { toast.error("Already added"); return; }
    setOrigins(prev => [...prev, url]);
    setNewOrigin("");
  };

  const removeOrigin = (url) => setOrigins(prev => prev.filter(o => o !== url));

  const save = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/settings/cors-origins`, origins);
      toast.success("Trusted origins saved — CORS updated instantly");
    } catch {
      toast.error("Failed to save origins");
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== "admin") return null;

  return (
    <Card className="bg-[#0c0c0e] border-white/5">
      <CardHeader>
        <CardTitle className="text-white font-mono flex items-center gap-2">
          <Lock className="w-5 h-5 text-green-400" /> Trusted Origins (CORS)
        </CardTitle>
        <CardDescription className="text-zinc-400">
          Only these URLs can make API requests to this app. Add your production domain here to lock down access.
          Changes take effect immediately — no restart needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {envOrigins.length > 0 && (
          <div className="space-y-2">
            <Label className="text-zinc-500 text-xs uppercase tracking-wider">From environment (.env)</Label>
            {envOrigins.map(o => (
              <div key={o} className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg">
                <span className="text-xs text-zinc-400 font-mono flex-1">{o}</span>
                <Badge variant="outline" className="text-[10px] text-zinc-500 border-zinc-700">env</Badge>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-zinc-500 text-xs uppercase tracking-wider">Trusted Origins ({origins.length})</Label>
          {origins.length === 0 ? (
            <div className="p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
              <p className="text-xs text-yellow-300 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" />
                No origins set — currently allowing ALL origins (*). Add your production URL to restrict access.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {origins.map(o => (
                <div key={o} className="flex items-center gap-2 px-3 py-2 bg-green-500/5 border border-green-500/10 rounded-lg">
                  <span className="text-xs text-green-300 font-mono flex-1">{o}</span>
                  <button onClick={() => removeOrigin(o)} className="text-zinc-500 hover:text-red-400 p-0.5" data-testid={`remove-origin-${o}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Input value={newOrigin} onChange={e => setNewOrigin(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addOrigin()}
            placeholder="https://yourdomain.com"
            className="bg-black/50 border-white/10 text-white font-mono text-xs"
            data-testid="new-origin-input" />
          <Button variant="outline" onClick={addOrigin}
            className="border-white/10 text-white hover:bg-white/10 shrink-0">
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-2.5 bg-blue-500/5 border border-blue-500/10 rounded-lg text-[11px] text-blue-300">
          <strong>Tip:</strong> Add your production URL (e.g. <code>https://admin.yourdomain.com</code>) and your Emergent preview URL.
          After saving, only these domains will be able to access this API.
        </div>

        <Button className="bg-green-600 hover:bg-green-700 text-white w-full" onClick={save} disabled={loading}
          data-testid="save-cors-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
          Save Trusted Origins
        </Button>
      </CardContent>
    </Card>
  );
}


function TwoFactorSection() {
  const { user } = useAuth();
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [copiedCode, setCopiedCode] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(user?.two_factor_enabled || false);

  const setup2FA = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to setup 2FA");
    } finally {
      setLoading(false);
    }
  };

  const verify2FA = async () => {
    if (verifyCode.length !== 6) {
      toast.error("Enter a 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/verify`, { code: verifyCode });
      setBackupCodes(res.data.backup_codes || []);
      setShowBackupCodes(true);
      setSetupData(null);
      setIs2FAEnabled(true);
      toast.success("2FA enabled successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
      setVerifyCode("");
    }
  };

  const disable2FA = async () => {
    if (disableCode.length < 6) {
      toast.error("Enter your TOTP code");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code: disableCode });
      setIs2FAEnabled(false);
      toast.success("2FA disabled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
      setDisableCode("");
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid="2fa-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" />
              Two-Factor Authentication
            </CardTitle>
            <CardDescription className="text-zinc-400 mt-1">
              Add an extra layer of security to your account
            </CardDescription>
          </div>
          <Badge className={is2FAEnabled ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30"}>
            {is2FAEnabled ? "Enabled" : "Disabled"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!is2FAEnabled && !setupData && (
          <Button onClick={setup2FA} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="setup-2fa-btn">
            <Key className="w-4 h-4 mr-2" />
            {loading ? "Setting up..." : "Enable 2FA"}
          </Button>
        )}

        {setupData && (
          <div className="space-y-4">
            <div className="p-4 bg-black/40 rounded-lg border border-white/5 text-center">
              <p className="text-sm text-zinc-400 mb-3">Scan this QR code with your authenticator app</p>
              <img 
                src={`data:image/png;base64,${setupData.qr_code}`}
                alt="2FA QR Code"
                className="mx-auto w-48 h-48 rounded-lg"
                data-testid="2fa-qr-code"
              />
              <p className="text-xs text-zinc-500 mt-3 font-mono break-all">Secret: {setupData.secret}</p>
            </div>
            <div className="flex gap-2">
              <Input
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="bg-black/50 border-white/10 text-white text-center font-mono tracking-widest"
                maxLength={6}
                data-testid="2fa-verify-input"
              />
              <Button onClick={verify2FA} disabled={loading} className="bg-green-600 hover:bg-green-700" data-testid="2fa-verify-btn">
                {loading ? "Verifying..." : "Verify"}
              </Button>
            </div>
          </div>
        )}

        {is2FAEnabled && (
          <div className="space-y-3">
            <p className="text-sm text-zinc-400">To disable 2FA, enter your current TOTP code:</p>
            <div className="flex gap-2">
              <Input
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="bg-black/50 border-white/10 text-white text-center font-mono tracking-widest"
                maxLength={6}
                data-testid="2fa-disable-input"
              />
              <Button onClick={disable2FA} disabled={loading} variant="destructive" data-testid="2fa-disable-btn">
                {loading ? "Disabling..." : "Disable 2FA"}
              </Button>
            </div>
          </div>
        )}

        {/* Backup Codes Dialog */}
        <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
          <DialogContent className="bg-[#0c0c0e] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                Save Your Backup Codes
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                These codes can be used if you lose access to your authenticator. Save them securely!
              </DialogDescription>
            </DialogHeader>
            <div className="bg-black/60 rounded-lg p-4 font-mono text-sm space-y-1" data-testid="backup-codes">
              {backupCodes.map((code, i) => (
                <div key={i} className="text-green-400">{code}</div>
              ))}
            </div>
            <Button onClick={copyBackupCodes} variant="outline" className="border-white/10 text-white" data-testid="copy-backup-codes-btn">
              {copiedCode ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copiedCode ? "Copied!" : "Copy All Codes"}
            </Button>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function ChangePasswordSection() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = async () => {
    if (!form.current_password || !form.new_password) {
      toast.error("All fields required");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }
    if (form.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: form.current_password,
        new_password: form.new_password
      });
      toast.success("Password changed successfully");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid="change-password-section">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Key className="w-5 h-5 text-blue-400" />
          Change Password
        </CardTitle>
        <CardDescription className="text-zinc-400 mt-1">Update your account password</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          <Label className="text-zinc-400 text-xs">Current Password</Label>
          <Input type="password" value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            placeholder="Enter current password"
            className="bg-black/50 border-white/10 text-white"
            data-testid="current-password-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs">New Password</Label>
            <Input type="password" value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              placeholder="Min 6 characters"
              className="bg-black/50 border-white/10 text-white"
              data-testid="new-password-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs">Confirm Password</Label>
            <Input type="password" value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              placeholder="Repeat new password"
              className="bg-black/50 border-white/10 text-white"
              data-testid="confirm-password-input" />
          </div>
        </div>
        <Button onClick={handleChange} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="change-password-btn">
          {loading ? "Changing..." : "Change Password"}
        </Button>
      </CardContent>
    </Card>
  );
}

function EmailConfigSection() {
  const { user } = useAuth();
  const [config, setConfig] = useState({
    smtp_server: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    from_email: ""
  });
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") fetchConfig();
  }, [user]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/settings/email`);
      setConfigured(res.data.configured);
      if (res.data.smtp_server) {
        setConfig(prev => ({
          ...prev,
          smtp_server: res.data.smtp_server,
          smtp_port: res.data.smtp_port,
          from_email: res.data.from_email
        }));
      }
    } catch (err) {
      console.error("Failed to fetch email config:", err);
    }
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings/email`, config);
      setConfigured(true);
      toast.success("Email configuration saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save email config");
    } finally {
      setLoading(false);
    }
  };

  const testEmail = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/settings/email/test`);
      if (res.data.success) {
        toast.success("Test email sent to your address");
      } else {
        toast.error("Failed to send test email");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email test failed");
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <Card className="bg-[#0c0c0e] border-white/5">
        <CardContent className="py-8 text-center text-zinc-500">
          Admin access required to configure email settings.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid="email-config-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              Email Notifications (SMTP)
            </CardTitle>
            <CardDescription className="text-zinc-400 mt-1">
              Configure SMTP for approval and scan notifications
            </CardDescription>
          </div>
          <Badge className={configured ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30"}>
            {configured ? "Configured" : "Not Configured"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-zinc-400">SMTP Server</Label>
            <Input
              value={config.smtp_server}
              onChange={(e) => setConfig({ ...config, smtp_server: e.target.value })}
              placeholder="smtp.gmail.com"
              className="bg-black/50 border-white/10 text-white"
              data-testid="smtp-server-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400">SMTP Port</Label>
            <Input
              type="number"
              value={config.smtp_port}
              onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) || 587 })}
              className="bg-black/50 border-white/10 text-white"
              data-testid="smtp-port-input"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-400">SMTP Username</Label>
          <Input
            value={config.smtp_username}
            onChange={(e) => setConfig({ ...config, smtp_username: e.target.value })}
            placeholder="your-email@gmail.com"
            className="bg-black/50 border-white/10 text-white"
            data-testid="smtp-username-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-400">SMTP Password / App Password</Label>
          <Input
            type="password"
            value={config.smtp_password}
            onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
            placeholder="••••••••"
            className="bg-black/50 border-white/10 text-white"
            data-testid="smtp-password-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-400">From Email</Label>
          <Input
            value={config.from_email}
            onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
            placeholder="noreply@yourdomain.com"
            className="bg-black/50 border-white/10 text-white"
            data-testid="from-email-input"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={saveConfig} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="save-email-config-btn">
            {loading ? "Saving..." : "Save Configuration"}
          </Button>
          {configured && (
            <Button onClick={testEmail} disabled={loading} variant="outline" className="border-white/10 text-white" data-testid="test-email-btn">
              <Send className="w-4 h-4 mr-2" />
              Send Test Email
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function WebhooksSection() {
  const { user } = useAuth();
  const [webhooks, setWebhooks] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    name: "",
    url: "",
    type: "generic",
    events: ["approval_pending"]
  });

  const eventOptions = [
    { value: "approval_pending", label: "Approval Pending" },
    { value: "approval_approved", label: "Approval Approved" },
    { value: "approval_rejected", label: "Approval Rejected" },
    { value: "scan_completed", label: "Scan Completed" }
  ];

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "senior_admin") fetchWebhooks();
  }, [user]);

  const fetchWebhooks = async () => {
    try {
      const res = await axios.get(`${API}/webhooks`);
      setWebhooks(res.data);
    } catch (err) {
      console.error("Failed to fetch webhooks:", err);
    }
  };

  const createWebhook = async () => {
    if (!newWebhook.name || !newWebhook.url) {
      toast.error("Name and URL are required");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/webhooks`, newWebhook);
      toast.success("Webhook created");
      setShowCreate(false);
      setNewWebhook({ name: "", url: "", type: "generic", events: ["approval_pending"] });
      fetchWebhooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create webhook");
    } finally {
      setLoading(false);
    }
  };

  const toggleWebhook = async (id, enabled) => {
    try {
      await axios.put(`${API}/webhooks/${id}?enabled=${!enabled}`);
      fetchWebhooks();
    } catch (err) {
      toast.error("Failed to update webhook");
    }
  };

  const deleteWebhook = async (id) => {
    try {
      await axios.delete(`${API}/webhooks/${id}`);
      toast.success("Webhook deleted");
      fetchWebhooks();
    } catch (err) {
      toast.error("Failed to delete webhook");
    }
  };

  const testWebhook = async (id) => {
    try {
      const res = await axios.post(`${API}/webhooks/${id}/test`);
      if (res.data.success) {
        toast.success("Test webhook sent!");
      } else {
        toast.error("Webhook test failed");
      }
    } catch (err) {
      toast.error("Webhook test failed");
    }
  };

  const toggleEvent = (event) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  if (user?.role !== "admin" && user?.role !== "senior_admin") {
    return (
      <Card className="bg-[#0c0c0e] border-white/5">
        <CardContent className="py-8 text-center text-zinc-500">
          Admin or senior admin access required for webhook configuration.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid="webhooks-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-400" />
              Webhook Integrations
            </CardTitle>
            <CardDescription className="text-zinc-400 mt-1">
              Send notifications to Slack, Discord, Teams, or custom endpoints
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-webhook-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Webhook
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {webhooks.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-4">No webhooks configured</p>
        ) : (
          webhooks.map((wh) => (
            <div key={wh.id} className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-white/5" data-testid={`webhook-item-${wh.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-medium">{wh.name}</span>
                  <Badge className="text-xs bg-zinc-800 text-zinc-400 border-zinc-700">{wh.type}</Badge>
                  <Badge className={wh.enabled ? "text-xs bg-green-500/20 text-green-400 border-green-500/30" : "text-xs bg-red-500/20 text-red-400 border-red-500/30"}>
                    {wh.enabled ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <p className="text-xs text-zinc-500 truncate mt-1">{wh.url}</p>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <Button size="sm" variant="ghost" onClick={() => testWebhook(wh.id)} className="text-zinc-400 hover:text-blue-400 h-8 w-8 p-0" data-testid={`test-webhook-${wh.id}`}>
                  <TestTube className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => toggleWebhook(wh.id, wh.enabled)} className="text-zinc-400 hover:text-yellow-400 h-8 w-8 p-0" data-testid={`toggle-webhook-${wh.id}`}>
                  {wh.enabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteWebhook(wh.id)} className="text-zinc-400 hover:text-red-400 h-8 w-8 p-0" data-testid={`delete-webhook-${wh.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}

        {/* Create Webhook Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-[#0c0c0e] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Create Webhook</DialogTitle>
              <DialogDescription className="text-zinc-400">Configure a new webhook endpoint</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Name</Label>
                <Input
                  value={newWebhook.name}
                  onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                  placeholder="My Slack Webhook"
                  className="bg-black/50 border-white/10 text-white"
                  data-testid="webhook-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">URL</Label>
                <Input
                  value={newWebhook.url}
                  onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                  placeholder="https://hooks.slack.com/services/..."
                  className="bg-black/50 border-white/10 text-white"
                  data-testid="webhook-url-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Type</Label>
                <Select value={newWebhook.type} onValueChange={(v) => setNewWebhook({ ...newWebhook, type: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="webhook-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0c0c0e] border-white/10">
                    <SelectItem value="generic">Generic</SelectItem>
                    <SelectItem value="slack">Slack</SelectItem>
                    <SelectItem value="discord">Discord</SelectItem>
                    <SelectItem value="teams">Microsoft Teams</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Events</Label>
                <div className="flex flex-wrap gap-2">
                  {eventOptions.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleEvent(opt.value)}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                        newWebhook.events.includes(opt.value)
                          ? 'bg-blue-600/20 text-blue-400 border-blue-500/30'
                          : 'bg-black/30 text-zinc-500 border-white/5 hover:border-white/20'
                      }`}
                      data-testid={`webhook-event-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={createWebhook} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="create-webhook-submit">
                {loading ? "Creating..." : "Create Webhook"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function UsersManagementSection() {
  const { user } = useAuth();
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "junior_admin" });

  useEffect(() => {
    if (user?.role === "admin") fetchUsers();
  }, [user]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/auth/users`);
      setUsersList(res.data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  const addUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error("All fields are required");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, newUser);
      toast.success(`User ${newUser.email} created`);
      setShowAddUser(false);
      setNewUser({ email: "", name: "", password: "", role: "junior_admin" });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/auth/users/${userId}/role?new_role=${newRole}`);
      toast.success("Role updated");
      fetchUsers();
    } catch (err) {
      toast.error("Failed to update role");
    }
  };

  const handleResetPassword = async (userId) => {
    if (!resetPassword || resetPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await axios.put(`${API}/auth/users/${userId}/reset-password`, { new_password: resetPassword });
      toast.success("Password reset successfully");
      setResetUserId(null);
      setResetPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    }
  };

  if (user?.role !== "admin") {
    return (
      <Card className="bg-[#0c0c0e] border-white/5">
        <CardContent className="py-8 text-center text-zinc-500">Admin access required.</CardContent>
      </Card>
    );
  }

  const roleBadge = (role) => {
    const colors = {
      admin: "bg-red-500/20 text-red-400 border-red-500/30",
      senior_admin: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      junior_admin: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    };
    return colors[role] || "bg-zinc-500/20 text-zinc-400 border-zinc-500/30";
  };

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid="users-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              User Management
            </CardTitle>
            <CardDescription className="text-zinc-400 mt-1">
              Add users and manage roles (invitation only)
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowAddUser(!showAddUser)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn">
            <UserPlus className="w-4 h-4 mr-1" /> Add User
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {showAddUser && (
          <div className="p-4 bg-black/30 rounded-lg border border-white/5 space-y-3" data-testid="add-user-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Full Name</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="John Doe" className="bg-black/50 border-white/10 text-white" data-testid="new-user-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Email</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="user@company.com" className="bg-black/50 border-white/10 text-white" data-testid="new-user-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Password</Label>
                <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Initial password" className="bg-black/50 border-white/10 text-white" data-testid="new-user-password" />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0c0c0e] border-white/10">
                    <SelectItem value="junior_admin">Junior Admin</SelectItem>
                    <SelectItem value="senior_admin">Senior Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={addUser} disabled={loading} className="bg-green-600 hover:bg-green-700 w-full" data-testid="save-new-user-btn">
              {loading ? "Creating..." : "Create User"}
            </Button>
          </div>
        )}

        {usersList.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-4">No users found</p>
        ) : (
          usersList.map((u) => (
            <div key={u.id} className="p-3 bg-black/30 rounded-lg border border-white/5 space-y-2" data-testid={`user-item-${u.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-white text-sm font-medium">{u.name}</span>
                  <span className="text-zinc-500 text-sm ml-2">{u.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  {u.two_factor_enabled && (
                    <Badge className="text-[10px] bg-green-500/10 text-green-400 border-green-500/20">2FA</Badge>
                  )}
                  <Select value={u.role} onValueChange={(v) => updateRole(u.id, v)} disabled={u.id === user?.id}>
                    <SelectTrigger className={`w-36 h-7 text-xs border ${roleBadge(u.role)}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0c0c0e] border-white/10">
                      <SelectItem value="junior_admin">Junior Admin</SelectItem>
                      <SelectItem value="senior_admin">Senior Admin</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  {u.id !== user?.id && (
                    <Button size="sm" variant="ghost"
                      onClick={() => { setResetUserId(resetUserId === u.id ? null : u.id); setResetPassword(""); }}
                      className="text-zinc-400 hover:text-yellow-400 h-7 text-xs px-2"
                      data-testid={`reset-pw-toggle-${u.id}`}>
                      <Key className="w-3 h-3 mr-1" /> Reset PW
                    </Button>
                  )}
                </div>
              </div>
              {resetUserId === u.id && (
                <div className="flex gap-2 pt-1" data-testid={`reset-pw-form-${u.id}`}>
                  <Input type="password" value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="New password (min 6 chars)"
                    className="bg-black/50 border-white/10 text-white text-sm flex-1"
                    data-testid={`reset-pw-input-${u.id}`} />
                  <Button size="sm" onClick={() => handleResetPassword(u.id)}
                    className="bg-yellow-600 hover:bg-yellow-700 text-xs"
                    data-testid={`reset-pw-submit-${u.id}`}>
                    Reset
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function AutoApprovalSection() {
  const { user } = useAuth();
  const [threshold, setThreshold] = useState("none");
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") fetchSettings();
  }, [user]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/auto-execute`);
      setThreshold(res.data.threshold || "none");
      setFetched(true);
    } catch (err) {
      console.error("Failed to fetch auto-execute settings:", err);
    }
  };

  const saveThreshold = async (newThreshold) => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings/auto-execute?threshold=${newThreshold}`);
      setThreshold(newThreshold);
      toast.success(`Auto-approval threshold set to: ${newThreshold === "none" ? "Disabled" : newThreshold}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update threshold");
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <Card className="bg-[#0c0c0e] border-white/5">
        <CardContent className="py-8 text-center text-zinc-500">Admin access required to configure workflow settings.</CardContent>
      </Card>
    );
  }

  const thresholdOptions = [
    { value: "none", label: "Disabled", desc: "All commands require manual approval", color: "text-zinc-400" },
    { value: "low", label: "Low Risk Only", desc: "Low-risk commands are auto-approved on creation", color: "text-green-400" },
    { value: "medium", label: "Low + Medium Risk", desc: "Low and medium-risk commands are auto-approved", color: "text-yellow-400" },
  ];

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid="auto-approval-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              Auto-Approval Rules
            </CardTitle>
            <CardDescription className="text-zinc-400 mt-1">
              Automatically approve commands based on risk level threshold
            </CardDescription>
          </div>
          <Badge className={threshold !== "none"
            ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
            : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30"
          }>
            {threshold !== "none" ? `Active: ${threshold}` : "Disabled"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {thresholdOptions.map(opt => (
          <button
            key={opt.value}
            onClick={() => saveThreshold(opt.value)}
            disabled={loading}
            className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all text-left ${
              threshold === opt.value
                ? 'bg-blue-600/10 border-blue-500/30'
                : 'bg-black/30 border-white/5 hover:border-white/15'
            }`}
            data-testid={`auto-approve-${opt.value}`}
          >
            <div>
              <p className={`text-sm font-medium ${threshold === opt.value ? 'text-blue-400' : 'text-white'}`}>{opt.label}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{opt.desc}</p>
            </div>
            {threshold === opt.value && (
              <Check className="w-5 h-5 text-blue-400 shrink-0" />
            )}
          </button>
        ))}

        <div className="flex items-start gap-2 p-3 bg-yellow-500/5 rounded-lg border border-yellow-500/10 mt-4">
          <Info className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
          <p className="text-xs text-zinc-400">
            <span className="text-yellow-400 font-medium">Note:</span> High and critical risk commands always require manual approval regardless of this setting. Auto-approved commands go directly to "Approved" status and can be executed immediately.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function MetricsIntervalSection() {
  const { user } = useAuth();
  const [interval, setInterval] = useState(30);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") {
      axios.get(`${API}/settings/metrics-interval`).then(res => {
        setInterval(res.data.interval_minutes || 30);
      }).catch(() => {});
    }
  }, [user]);

  const saveInterval = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings/metrics-interval?interval_minutes=${interval}`);
      toast.success(`Metrics refresh set to ${interval} minutes`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== "admin") return null;

  const presets = [5, 15, 30, 60, 120, 360, 720, 1440];

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid="metrics-interval-section">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Timer className="w-5 h-5 text-blue-400" />
          Metrics Refresh Interval
        </CardTitle>
        <CardDescription className="text-zinc-400 mt-1">
          How often the dashboard auto-refreshes server metrics
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {presets.map(val => (
            <button key={val} onClick={() => setInterval(val)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                interval === val ? "bg-blue-600/20 text-blue-400 border-blue-500/30" : "bg-black/30 text-zinc-500 border-white/5 hover:border-white/20"
              }`} data-testid={`interval-${val}`}>
              {val < 60 ? `${val}m` : `${val / 60}h`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Input type="number" min={1} max={1440} value={interval}
            onChange={e => setInterval(parseInt(e.target.value) || 30)}
            className="bg-black/50 border-white/10 text-white w-24 text-center font-mono"
            data-testid="interval-input" />
          <span className="text-xs text-zinc-500">minutes</span>
          <Button onClick={saveInterval} disabled={loading} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="save-interval-btn">
            {loading ? "Saving..." : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AIProviderSection() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") fetchStatus();
  }, [user]);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/settings/ai-provider`);
      setStatus(res.data);
    } catch { /* ignore */ }
  };

  const handleSaveKey = async () => {
    if (!newKey.trim()) return;
    setSaving(true);
    setTestResult(null);
    try {
      const res = await axios.put(`${API}/settings/ai-provider/key?key=${encodeURIComponent(newKey.trim())}`);
      toast.success(res.data.message);
      setNewKey("");
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save key");
    } finally { setSaving(false); }
  };

  const handleRemoveKey = async () => {
    if (!window.confirm("Remove OpenAI key? AI will use Emergent LLM only.")) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/ai-provider/key?key=`);
      toast.success(res.data.message);
      setTestResult(null);
      fetchStatus();
    } catch { toast.error("Failed to remove key"); }
    finally { setSaving(false); }
  };

  const handleTestKey = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/settings/ai-provider/test`);
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.detail || "Test failed" });
    } finally { setTesting(false); }
  };

  if (user?.role !== "admin") return null;

  const providerColor = {
    openai: "text-green-400",
    emergent: "text-blue-400",
    emergent_fallback: "text-yellow-400",
    none: "text-red-400"
  };

  const providerLabel = {
    openai: "OpenAI (Primary)",
    emergent: "Emergent LLM",
    emergent_fallback: "Emergent LLM (Fallback)",
    none: "No Provider"
  };

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid="ai-provider-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              AI Provider
            </CardTitle>
            <CardDescription className="text-zinc-400 mt-1">
              Configure OpenAI API key for AI features
            </CardDescription>
          </div>
          {status && (
            <Badge className={`${
              status.active_provider === "openai"
                ? "bg-green-500/20 text-green-400 border-green-500/30"
                : status.active_provider === "emergent"
                ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                : "bg-red-500/20 text-red-400 border-red-500/30"
            }`} data-testid="ai-provider-badge">
              {providerLabel[status.active_provider] || status.active_provider}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current status */}
        {status && (
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-black/30 rounded-lg border border-white/5">
              <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">OpenAI Key</p>
              <p className={`text-sm font-mono ${status.openai_configured ? "text-green-400" : "text-zinc-500"}`} data-testid="openai-key-display">
                {status.openai_key_masked || "Not configured"}
              </p>
            </div>
            <div className="p-3 bg-black/30 rounded-lg border border-white/5">
              <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Emergent Fallback</p>
              <p className={`text-sm font-mono ${status.emergent_configured ? "text-green-400" : "text-zinc-500"}`}>
                {status.emergent_configured ? "Configured" : "Not available"}
              </p>
            </div>
          </div>
        )}

        {/* Update key */}
        <div className="space-y-2">
          <Label className="text-zinc-400 text-xs">Update OpenAI API Key</Label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                type={showKey ? "text" : "password"}
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                placeholder="sk-proj-..."
                className="bg-black/50 border-white/10 text-white font-mono text-sm pr-10"
                data-testid="openai-key-input"
              />
              <button
                onClick={() => setShowKey(!showKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button onClick={handleSaveKey} disabled={saving || !newKey.trim()} className="bg-blue-600 hover:bg-blue-700 shrink-0" data-testid="save-ai-key-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4 mr-1" />}
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button onClick={handleTestKey} disabled={testing} variant="outline"
            className="bg-white/5 border-white/10 hover:bg-white/10 text-white text-xs"
            data-testid="test-ai-key-btn">
            {testing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <TestTube className="w-3 h-3 mr-1" />}
            {testing ? "Testing..." : "Test Connection"}
          </Button>
          {status?.openai_configured && (
            <Button onClick={handleRemoveKey} disabled={saving} variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
              data-testid="remove-ai-key-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Remove Key
            </Button>
          )}
        </div>

        {/* Test result */}
        {testResult && (
          <div className={`p-3 rounded-lg border ${
            testResult.success
              ? "bg-green-500/5 border-green-500/20"
              : "bg-red-500/5 border-red-500/20"
          }`} data-testid="ai-test-result">
            <div className="flex items-center gap-2 mb-1">
              {testResult.success ? <Check className="w-4 h-4 text-green-400" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
              <span className={`text-sm font-medium ${testResult.success ? "text-green-400" : "text-red-400"}`}>
                {testResult.success ? "Connection successful" : "Connection failed"}
              </span>
            </div>
            <p className="text-xs text-zinc-400">
              {testResult.success
                ? `Provider: ${testResult.provider}${testResult.model ? ` (${testResult.model})` : ""}${testResult.note ? ` — ${testResult.note}` : ""}`
                : testResult.error}
            </p>
          </div>
        )}

        {/* Info note */}
        <div className="flex items-start gap-2 p-3 bg-blue-500/5 rounded-lg border border-blue-500/10">
          <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
          <p className="text-xs text-zinc-400">
            <span className="text-blue-400 font-medium">How it works:</span> When an OpenAI key is configured, it's used as the primary AI provider. If it fails (quota exceeded, etc.), the system automatically falls back to the Emergent LLM Key. Both work with GPT-4o.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function PasskeySection() {
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);

  const load = () => axios.get(`${API}/auth/passkey/credentials`).then(r => setCreds(r.data)).catch(() => {});
  useEffect(() => {
    import("@simplewebauthn/browser").then(m => setSupported(m.browserSupportsWebAuthn()));
    load();
  }, []);

  const addPasskey = async () => {
    setLoading(true);
    try {
      const { startRegistration } = await import("@simplewebauthn/browser");
      const opts = (await axios.post(`${API}/auth/passkey/registration/options`)).data;
      const att = await Promise.race([
        startRegistration({ optionsJSON: opts }),
        new Promise((_, rej) => setTimeout(() => rej(new Error("Passkey prompt timed out — try again")), 60000)),
      ]);
      await axios.post(`${API}/auth/passkey/registration/verify`, att);
      toast.success("Passkey registered");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Passkey registration failed/cancelled");
    } finally {
      setLoading(false);
    }
  };

  const removePasskey = async (id) => {
    if (!window.confirm("Remove this passkey?")) return;
    try {
      await axios.delete(`${API}/auth/passkey/credentials/${encodeURIComponent(id)}`);
      toast.success("Passkey removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove passkey");
    }
  };

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid="passkey-section">
      <CardHeader>
        <CardTitle className="text-white font-mono flex items-center gap-2">
          <Key className="w-5 h-5 text-cyan-400" /> Passkeys (WebAuthn)
        </CardTitle>
        <CardDescription className="text-zinc-400">
          Sign in with Face ID, Touch ID, Windows Hello, or a hardware security key — no password needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!supported && (
          <p className="text-xs text-yellow-300 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> This browser doesn't support passkeys.
          </p>
        )}
        {creds.length === 0 ? (
          <p className="text-sm text-zinc-500">No passkeys yet. Add one to enable passwordless sign-in.</p>
        ) : (
          <div className="space-y-2">
            {creds.map(c => (
              <div key={c.credential_id} className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg"
                data-testid={`passkey-item`}>
                <Key className="w-4 h-4 text-cyan-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{c.nickname || "Passkey"}</p>
                  <p className="text-[10px] text-zinc-500">Added {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</p>
                </div>
                <button onClick={() => removePasskey(c.credential_id)} className="text-zinc-500 hover:text-red-400 p-1"
                  data-testid="remove-passkey-btn">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <Button onClick={addPasskey} disabled={loading || !supported}
          className="bg-cyan-600 hover:bg-cyan-500 text-white w-full" data-testid="add-passkey-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          Add a Passkey
        </Button>
      </CardContent>
    </Card>
  );
}

function IpAllowlistSection() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [cidrs, setCidrs] = useState([]);
  const [newCidr, setNewCidr] = useState("");
  const [yourIp, setYourIp] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    axios.get(`${API}/security/ip-allowlist`).then(r => {
      setEnabled(r.data.enabled); setCidrs(r.data.cidrs || []); setYourIp(r.data.your_ip || "");
    }).catch(() => {});
  }, [user]);

  const addCidr = () => {
    const c = newCidr.trim();
    if (!c || cidrs.includes(c)) return;
    setCidrs(prev => [...prev, c]); setNewCidr("");
  };

  const save = async () => {
    setLoading(true);
    try {
      const r = await axios.put(`${API}/security/ip-allowlist`, { enabled, cidrs });
      setCidrs(r.data.cidrs); setEnabled(r.data.enabled);
      toast.success("Admin IP allowlist saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== "admin") return null;

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid="ip-allowlist-section">
      <CardHeader>
        <CardTitle className="text-white font-mono flex items-center gap-2">
          <Globe className="w-5 h-5 text-orange-400" /> Admin IP Allowlist
        </CardTitle>
        <CardDescription className="text-zinc-400">
          When enabled, admins can only sign in from these IPs/CIDRs. Your current IP is auto-added on save so you can't lock yourself out.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between bg-black/40 rounded p-2">
          <div>
            <p className="text-sm text-white">Enforce IP allowlist for admin logins</p>
            <p className="text-[11px] text-zinc-500">Your IP: <span className="font-mono text-zinc-400">{yourIp}</span></p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="ip-allowlist-toggle" />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-500 text-xs uppercase tracking-wider">Allowed IPs / CIDRs ({cidrs.length})</Label>
          {cidrs.length === 0 ? (
            <p className="text-xs text-zinc-600">Empty = allow all (feature effectively off).</p>
          ) : cidrs.map(c => (
            <div key={c} className="flex items-center gap-2 px-3 py-2 bg-orange-500/5 border border-orange-500/10 rounded-lg">
              <span className="text-xs text-orange-300 font-mono flex-1">{c}</span>
              <button onClick={() => setCidrs(prev => prev.filter(x => x !== c))} className="text-zinc-500 hover:text-red-400 p-0.5"
                data-testid={`remove-cidr-${c}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newCidr} onChange={e => setNewCidr(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addCidr()}
            placeholder="203.0.113.10  or  203.0.113.0/24"
            className="bg-black/50 border-white/10 text-white font-mono text-xs" data-testid="new-cidr-input" />
          <Button variant="outline" onClick={addCidr} className="border-white/10 text-white hover:bg-white/10 shrink-0">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <Button className="bg-orange-600 hover:bg-orange-500 text-white w-full" onClick={save} disabled={loading}
          data-testid="save-ip-allowlist-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
          Save Allowlist
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-white font-mono">Settings</h1>
        <p className="text-zinc-400 text-sm mt-1">Manage security, notifications, and integrations</p>
      </div>

      <Tabs defaultValue="security" className="w-full">
        <TabsList className="bg-black/30 border border-white/5">
          <TabsTrigger value="security" className="data-[state=active]:bg-blue-600" data-testid="settings-security-tab">
            <Shield className="w-4 h-4 mr-2" /> Security
          </TabsTrigger>
          <TabsTrigger value="access" className="data-[state=active]:bg-blue-600" data-testid="settings-access-tab">
            <Globe className="w-4 h-4 mr-2" /> Access
          </TabsTrigger>
          <TabsTrigger value="workflow" className="data-[state=active]:bg-blue-600" data-testid="settings-workflow-tab">
            <Zap className="w-4 h-4 mr-2" /> Workflow
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-blue-600" data-testid="settings-users-tab">
            <Users className="w-4 h-4 mr-2" /> Users
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-blue-600" data-testid="settings-email-tab">
            <Mail className="w-4 h-4 mr-2" /> Email
          </TabsTrigger>
          <TabsTrigger value="webhooks" className="data-[state=active]:bg-blue-600" data-testid="settings-webhooks-tab">
            <Bell className="w-4 h-4 mr-2" /> Webhooks
          </TabsTrigger>
          <TabsTrigger value="emergency" className="data-[state=active]:bg-red-600" data-testid="settings-emergency-tab">
            <AlertTriangle className="w-4 h-4 mr-2" /> Emergency
          </TabsTrigger>
        </TabsList>

        <TabsContent value="security" className="mt-4 space-y-4">
          <TwoFactorSection />
          <PasskeySection />
          <ChangePasswordSection />
        </TabsContent>
        <TabsContent value="emergency" className="mt-4 space-y-4">
          <EmergencyPanel />
        </TabsContent>
        <TabsContent value="access" className="mt-4 space-y-4">
          <CORSOriginsSection />
          <IpAllowlistSection />
        </TabsContent>
        <TabsContent value="workflow" className="mt-4 space-y-4">
          <AIProviderSection />
          <AutoApprovalSection />
          <MetricsIntervalSection />
        </TabsContent>
        <TabsContent value="users" className="mt-4">
          <UsersManagementSection />
        </TabsContent>
        <TabsContent value="email" className="mt-4">
          <EmailConfigSection />
        </TabsContent>
        <TabsContent value="webhooks" className="mt-4">
          <WebhooksSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
